// CORE
import { useAppSelector } from 'hooks/redux';
import React, { useMemo } from 'react';

// DATA
import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import { useBillersWithLogos } from 'app-components/flux/bill-presentment/billPresentmentHooks';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';

// MUI
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import ConnectedIcon from './ConnectedIcon';

const useStyles = makeStyles<{ size: number; avatarColor: string; textColor?: string }>()(
  (theme, { size, avatarColor, textColor }) => ({
    avatar: {
      width: size,
      height: size,
      backgroundColor: avatarColor || theme.palette.background.layer2,
      color: textColor || theme.palette.common.white,
      borderRadius: size / 4,
      position: 'relative',
      overflow: 'visible',
      flexGrow: 0,

      fontSize: 24 + Math.round((size - 48) / (size > 48 ? 4 : 2.5)),
      fontWeight: (4 + Math.round(size / 20)) * 100,
    },

    hide: {
      backgroundColor: theme.applyOpacityToHex?.(theme.palette.background.paper, 0),
      color: theme.applyOpacityToHex?.(theme.palette.background.paper, 0),
    },

    img: {
      width: size,
      height: size,
      borderRadius: size / 4,
    },
  }),
);

interface ConnectedAvatarProps {
  model?: ScheduledTransactionType;
  txn?: TransactionProps | TransactionRecord;
  size?: number;
  iconSize?: number;
  color?: string;
  textColor?: string;
  className?: string;
  isCurrentCategoryIncome?: boolean;
}
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ConnectedAvatar(props: ConnectedAvatarProps) {
  const { model, size = 40, txn, iconSize = 20, color, className, textColor, isCurrentCategoryIncome } = props;

  const theme = useTheme();

  const showIcon = model?.presentmentAccountId;
  let logo;
  let logoName;

  const fiBiller = useAppSelector((state) =>
    billPresentmentSelectors.selectBillerForAccountId(state, model?.presentmentAccountId || ''),
  );
  const { billersWithLogos } = useBillersWithLogos(fiBiller ? [fiBiller] : []);
  if (model?.presentmentAccountId && billersWithLogos?.length) {
    logo = billersWithLogos[0]?.logoKey;
    logoName = fiBiller?.name;
  }

  // BVA stuff
  const displayTxn = useMemo(() => txn || model?.transaction || ({} as TransactionProps), [txn, model]);

  const creditAccount = useAppSelector((state) => transactionsSelectors.getCreditAccountFromTxn(state, displayTxn));
  const institution = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionForInstitutionLogin(state, creditAccount?.institutionLoginId || ''),
  );

  const { autoAdjustAmount, autoAdjustDueOn } = model || {};

  const hasBVA = Boolean(
    creditAccount &&
      transactionsUtils.isTransferTxn(displayTxn) &&
      ((autoAdjustAmount && autoAdjustAmount !== AutoAdjustAmountEnum.none) || autoAdjustDueOn),
  );

  if (displayTxn && institution && transactionsUtils.isTransferTxn(displayTxn) && creditAccount) {
    if (institution?.logoUrl) {
      logo = institution.logoUrl;
      logoName = institution.name;
    } else if (institution?.logo) {
      logo = `data:image/svg;base64, ${institution.logo}`;
      logoName = institution.name;
    }
  }

  const text = useMemo(
    () =>
      displayTxn.type === 'INVESTMENT'
        ? (
            (displayTxn.investmentTxnType &&
              transactionsUtils.InvestmentTxnActionsByType[displayTxn.investmentTxnType]) ||
            '?'
          ).toUpperCase?.()[0]
        : (displayTxn.payee || '?').toUpperCase?.()[0],
    [displayTxn],
  );
  const isIncomeAvatar = useAppSelector((state) => transactionsSelectors.isIncomeTxn(state, displayTxn));
  const isIncome = isCurrentCategoryIncome === undefined ? isIncomeAvatar : isCurrentCategoryIncome;
  let avatarColor = color || (isIncome ? theme.palette.legend.income : theme.palette.legend.bill);
  if (model?.isCompleted) avatarColor = theme.palette.icon.light;

  const { classes, cx } = useStyles({ size, avatarColor, textColor });

  return (
    <Avatar className={cx(classes.avatar, className, logo && classes.hide)}>
      {logo ? <img alt={logoName} src={logo} className={classes.img} /> : text}

      {(showIcon || hasBVA) && <ConnectedIcon model={model} size={iconSize} txn={txn} hasBVA={hasBVA} />}
    </Avatar>
  );
}

ConnectedAvatar.displayName = 'ConnectedAvatar';

export default ConnectedAvatar;
